import { Transform, Type } from 'class-transformer';
import {
  ArrayMinSize,
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsDateString,
  IsEnum,
  IsIn,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import {
  ScheduleAvailabilityTypeEnum,
  ISchedule,
  ITag,
  OrderMode,
  isPromotionCode,
  IPromotionAction,
  IPromotionCondition,
  PromotionConditionType,
  PromotionActionType,
  IConnector,
  PromotionScope,
  IShop,
} from '../../interfaces';
import { MongodbRelationId, TypeORMRelationId } from '../utils.dto';
import { ScheduleDTO } from './schedule.dto';
import {
  PromotionConditionDTO,
  PromotionConditionBirthdayWindowDetailsDTO,
  PromotionConditionCustomersDetailsDTO,
  PromotionConditionPacksDetailsDTO,
  PromotionConditionProductVariationsDetailsDTO,
  PromotionConditionMinOrderTotalDetailsDTO,
} from './promotion-condition.dto';
import {
  PromotionActionAddPackDetailsDTO,
  PromotionActionAddProductVariationDetailsDTO,
  PromotionActionDTO,
  PromotionActionItemsDiscountFixedDetailsDTO,
  PromotionActionItemsDiscountPercentageDetailsDTO,
  PromotionActionOrderDiscountFixedDetailsDTO,
  PromotionActionOrderDiscountPercentageDetailsDTO,
} from './promotion-action.dto';

export class PromotionDTO {
  @IsNotEmpty({ message: 'Le nom de la promotion est requis' })
  name: string;

  @Type(() => Object)
  image: any;

  @IsOptional()
  @IsArray()
  @IsIn(Object.values(PromotionScope), {
    each: true,
    message: "Ce scope d'affichage n'existe pas",
  })
  scopes?: PromotionScope[];

  @IsNotEmpty({ message: 'La description de la promotion est requise' })
  description: string;

  @IsOptional()
  isPromotionCode?: boolean;

  @ValidateIf((dto: PromotionDTO) => !!dto.isPromotionCode || isPromotionCode(dto))
  @IsNotEmpty({ message: 'Le code est requis' })
  code?: string;

  @IsOptional()
  @Transform((value) => (typeof value === 'string' ? +value : value))
  @IsNumber({}, { message: 'La priorité est invalide' })
  priority?: number;

  @ValidateIf((dto: PromotionDTO) => !!dto.isPromotionCode || isPromotionCode(dto))
  @IsNotEmpty({ message: 'Le connecteur est requis pour une promotion avec code' })
  @IsArray()
  @ArrayMinSize(1, { message: 'Au moins un connecteur est requis' })
  @ValidateNested({ each: true })
  @Type(() => TypeORMRelationId)
  connectors?: IConnector[];

  @IsOptional()
  imageUrl?: string;

  @IsOptional()
  disabled: boolean;

  @IsOptional()
  @IsNumber({}, { message: 'Le nombre de fois que la promotion peut être utilisée est invalide' })
  maxUses?: number;

  @ArrayNotEmpty({ message: 'Le mode de livraison est requis' })
  @ArrayMinSize(1, { message: 'Au moins un mode de livraison est requis' })
  @IsIn(Object.values(OrderMode), {
    message: "Ce mode de livraison n'existe pas",
    each: true,
  })
  orderModes: OrderMode[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => TypeORMRelationId)
  tags?: ITag[];

  @IsOptional()
  @IsBoolean({ message: 'La cumulabilité est invalide' })
  cumulative?: boolean;

  @IsOptional()
  @IsBoolean({ message: 'La répétabilité est invalide' })
  repeatable?: boolean;

  @IsOptional()
  @IsBoolean({ message: 'La disponibilité unique par client est invalide' })
  uniqueByCustomer?: boolean;

  @IsOptional()
  @IsDateString({ message: 'La date de début est invalide' })
  startDate?: Date;

  @IsOptional()
  @IsDateString({ message: 'La date de fin est invalide' })
  endDate?: Date;

  @ValidateIf((dto: PromotionDTO) => !!dto.shops?.length)
  @ArrayMinSize(1, {
    message: 'Au moins un restaurant est requis',
  })
  @ValidateNested({ each: true })
  @Type(() => MongodbRelationId)
  shops?: Pick<IShop, '_id' | 'internalName' | 'publicName'>[];

  @IsEnum(ScheduleAvailabilityTypeEnum, { message: 'Le type de disponibilité est invalide' })
  availabilityType: ScheduleAvailabilityTypeEnum;

  @IsOptional()
  @ValidateNested()
  @Type(() => ScheduleDTO)
  availabilitySchedule?: ISchedule;

  @ValidateNested({
    each: true,
    message: 'La configuration de la condition est invalide',
  })
  @Type(() => PromotionConditionDTO, {
    keepDiscriminatorProperty: true,
    discriminator: {
      property: 'type',
      subTypes: [
        {
          value: PromotionConditionBirthdayWindowDetailsDTO,
          name: PromotionConditionType.BIRTHDAY_WINDOW,
        },
        { value: PromotionConditionCustomersDetailsDTO, name: PromotionConditionType.CUSTOMERS },
        { value: PromotionConditionPacksDetailsDTO, name: PromotionConditionType.PACKS },
        {
          value: PromotionConditionProductVariationsDetailsDTO,
          name: PromotionConditionType.PRODUCT_VARIATIONS,
        },
        // { value: PromotionConditionTagsDetailsDTO, name: PromotionConditionType.TAGS },
        {
          value: PromotionConditionMinOrderTotalDetailsDTO,
          name: PromotionConditionType.ORDER_TOTAL,
        },
      ],
    },
  })
  conditions?: IPromotionCondition[];

  @ValidateNested({
    each: true,
    message: 'La configuration de la condition est invalide',
  })
  @ArrayMinSize(1, { message: 'Au moins une action est requise' })
  @Type(() => PromotionActionDTO, {
    keepDiscriminatorProperty: true,
    discriminator: {
      property: 'type',
      subTypes: [
        {
          value: PromotionActionAddProductVariationDetailsDTO,
          name: PromotionActionType.ADD_PRODUCT_VARIATION,
        },
        { value: PromotionActionAddPackDetailsDTO, name: PromotionActionType.ADD_PACK },
        {
          value: PromotionActionOrderDiscountFixedDetailsDTO,
          name: PromotionActionType.ORDER_DISCOUNT_FIXED,
        },
        {
          value: PromotionActionOrderDiscountPercentageDetailsDTO,
          name: PromotionActionType.ORDER_DISCOUNT_PERCENTAGE,
        },
        {
          value: PromotionActionItemsDiscountFixedDetailsDTO,
          name: PromotionActionType.ITEMS_DISCOUNT_FIXED,
        },
        {
          value: PromotionActionItemsDiscountPercentageDetailsDTO,
          name: PromotionActionType.ITEMS_DISCOUNT_PERCENTAGE,
        },
      ],
    },
  })
  actions?: IPromotionAction[];
}
